import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Drawer, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faBriefcaseMedical, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import MemberCarePlanService from '../../../services/MemberCarePlanService';
import { useStore } from '../../../hooks/storeContext';
import ROUTES from '../../../constants/routes';
import { COLORS } from '../../../constants/colors';
import { MemberRecordContent } from '../../MemberRecordEdit';
import CarePlanTab from './CarePlanTab';
import CaseTab from './CaseTab';

const RISK_COLORS = { critical: 'volcano', high: 'orange', medium: 'gold', low: 'cyan', minimal: 'cyan' };

const formatPhone = (p) => {
  const digits = String(p || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : (p || '');
};

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

/**
 * Case Management side panel: the member's care plan and case, plus the full
 * member record tabs, without leaving the queue.
 */
const MemberCardDrawer = ({ patientId, name, open, onClose, onCaseChanged }) => {
  const { authStore } = useStore();
  const isMobile = useIsMobile();
  const [carePlan, setCarePlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await MemberCarePlanService.getCarePlan(patientId);
      if (res?.success) setCarePlan(res.data);
      else setError(res?.error || res?.message || 'Unable to load the care plan');
    } catch (e) {
      setError('Unable to load the care plan');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    setCarePlan(null);
    if (open) load();
  }, [open, load]);

  const handleChanged = () => {
    load();
    if (onCaseChanged) onCaseChanged();
  };

  const member = carePlan?.member;
  const riskLevel = member?.riskLevel;

  const extraTabs = [
    {
      key: 'carePlan',
      icon: <FontAwesomeIcon icon={faClipboardList} style={{ color: COLORS.$deepTiffany }} />,
      label: 'Care Plan',
      render: () => <CarePlanTab data={carePlan} loading={loading} error={error} />
    },
    {
      key: 'case',
      icon: <FontAwesomeIcon icon={faBriefcaseMedical} style={{ color: COLORS.$deepTiffany }} />,
      label: 'Case',
      render: () => (
        <CaseTab
          patientId={patientId}
          data={carePlan}
          loading={loading}
          error={error}
          currentUserId={authStore.me?.id}
          onChanged={handleChanged}
        />
      )
    }
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={isMobile ? '100%' : 'min(1100px, 92vw)'}
      destroyOnClose
      title={(
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span>{member?.name || name || 'Member'}</span>
          {riskLevel && riskLevel !== 'unknown' && (
            <Tag color={RISK_COLORS[riskLevel]} style={{ textTransform: 'capitalize' }}>
              {riskLevel}{member.riskScore != null ? ` · ${member.riskScore}/100` : ''}
            </Tag>
          )}
          {member?.phone && <span style={{ fontSize: 13, fontWeight: 400, color: COLORS.textLight }}>{formatPhone(member.phone)}</span>}
        </div>
      )}
      extra={patientId && (
        <Link to={ROUTES.ADMIN_MEMBER_RECORD_EDIT.replace(':patientId', patientId)} onClick={onClose}>
          Open full record <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ marginLeft: 4 }} />
        </Link>
      )}
    >
      {open && patientId && (
        <MemberRecordContent
          key={patientId}
          patientId={patientId}
          embedded
          extraTabs={extraTabs}
          defaultTabKey="carePlan"
        />
      )}
    </Drawer>
  );
};

export default MemberCardDrawer;
